import React, { useMemo } from 'react';
import { Task } from '../../../types/task';
import { PRIORITY_META, TASK_STATUSES, toDate, startOfDay, fmtDue } from './taskMeta';
import { CheckIcon, ChatBubbleLeftIcon, BriefcaseIcon, BuildingOfficeIcon, PlayIcon, UserIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

interface Props {
  tasks: Task[];            // már csak a sajátok
  onView: (task: Task) => void;
  onUpdateStatus: (taskId: string, status: Task['status']) => void;
  projectNames?: Record<string, string>;
}

type Bucket = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none' | 'done';
const BUCKETS: { id: Bucket; label: string; tone: string }[] = [
  { id: 'overdue', label: 'Lejárt', tone: 'text-rose-600 dark:text-rose-400' },
  { id: 'today', label: 'Ma', tone: 'text-amber-600 dark:text-amber-400' },
  { id: 'tomorrow', label: 'Holnap', tone: 'text-gray-700 dark:text-gray-200' },
  { id: 'week', label: 'Ezen a héten', tone: 'text-gray-700 dark:text-gray-200' },
  { id: 'later', label: 'Később', tone: 'text-gray-700 dark:text-gray-200' },
  { id: 'none', label: 'Határidő nélkül', tone: 'text-gray-500 dark:text-gray-400' },
  { id: 'done', label: 'Nemrég kész', tone: 'text-emerald-600 dark:text-emerald-400' }
];

const bucketOf = (t: Task): Bucket => {
  if (t.status === 'completed') return 'done';
  const d = toDate(t.dueDate);
  if (!d) return 'none';
  const today = startOfDay(new Date());
  const diff = Math.round((startOfDay(d).getTime() - today.getTime()) / 86400000);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  const dow = (today.getDay() + 6) % 7; // hétfő=0
  if (diff <= 6 - dow) return 'week';
  return 'later';
};

const MyWork: React.FC<Props> = ({ tasks, onView, onUpdateStatus, projectNames = {} }) => {
  const groups = useMemo(() => {
    const m: Record<Bucket, Task[]> = { overdue: [], today: [], tomorrow: [], week: [], later: [], none: [], done: [] };
    const rank = { urgent: 0, high: 1, medium: 2, low: 3 } as Record<string, number>;
    tasks.filter(t => t.status !== 'cancelled').forEach(t => m[bucketOf(t)].push(t));
    (Object.keys(m) as Bucket[]).forEach(k => m[k].sort((a, b) => {
      const da = toDate(a.dueDate)?.getTime() || 0, db = toDate(b.dueDate)?.getTime() || 0;
      return (k === 'done' ? 0 : da - db) || (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9);
    }));
    // csak az elmúlt 7 nap kész feladatai
    const weekAgo = Date.now() - 7 * 86400000;
    m.done = m.done.filter(t => (toDate(t.completedDate)?.getTime() || toDate(t.updatedAt)?.getTime() || 0) >= weekAgo).slice(0, 10);
    return m;
  }, [tasks]);

  const open = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled').length;
  if (open === 0 && groups.done.length === 0) {
    return <div className="crm-card px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">Nincs rád osztott nyitott feladat. 🎉</div>;
  }

  return (
    <div className="space-y-4">
      {BUCKETS.map(b => {
        const list = groups[b.id];
        if (!list.length) return null;
        return (
          <section key={b.id} className="crm-card overflow-hidden">
            <div className="px-3 py-2 border-b border-[var(--crm-border)] flex items-center gap-2">
              <span className={`text-xs font-semibold uppercase tracking-wide ${b.tone}`}>{b.label}</span>
              <span className="text-xs text-gray-400 tabular-nums">{list.length}</span>
            </div>
            <ul className="divide-y divide-[var(--crm-border)]">
              {list.map(t => {
                const pr = PRIORITY_META[t.priority] || PRIORITY_META.medium;
                const st = TASK_STATUSES.find(s => s.id === t.status);
                const done = t.status === 'completed';
                return (
                  <li key={t.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/60">
                    <button
                      type="button"
                      onClick={() => onUpdateStatus(t.id, done ? 'todo' : 'completed')}
                      title={done ? 'Visszanyitás' : 'Késznek jelölés'}
                      className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 dark:border-gray-600 hover:border-emerald-500 text-transparent hover:text-emerald-500'}`}
                    >
                      <CheckIcon className="w-3 h-3" />
                    </button>
                    <button type="button" onClick={() => onView(t)} className="min-w-0 flex-1 text-left">
                      <div className={`text-sm text-gray-900 dark:text-gray-50 truncate ${done ? 'line-through text-gray-400' : ''}`}>{t.title}</div>
                      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                        <span className="font-mono">{t.taskNumber}</span>
                        <span className={`inline-flex items-center gap-1 ${pr.text}`}><span className={`w-1.5 h-1.5 rounded-full ${pr.dot}`} />{pr.label}</span>
                        {st && t.status === 'in_progress' && <span className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400"><PlayIcon className="w-3 h-3" />{st.label}</span>}
                        {t.dueDate && b.id !== 'today' && b.id !== 'tomorrow' && <span>{fmtDue(t.dueDate)}</span>}
                        {t.projectId && projectNames[t.projectId] && <span className="inline-flex items-center gap-1 truncate max-w-[260px]" title={projectNames[t.projectId]}><BriefcaseIcon className="w-3 h-3 flex-shrink-0" />{projectNames[t.projectId]}</span>}
                        {t.customerName && <span className="inline-flex items-center gap-1 truncate max-w-[220px]" title={t.customerName}><BuildingOfficeIcon className="w-3 h-3 flex-shrink-0" />{t.customerName}</span>}
                        {t.assignedToName && <span className="inline-flex items-center gap-1 truncate max-w-[160px]" title={`Felelős: ${t.assignedToName}`}><UserIcon className="w-3 h-3 flex-shrink-0" />{t.assignedToName}</span>}
                        {(t.subtasks?.length || 0) > 0 && <span className="inline-flex items-center gap-1 tabular-nums" title="Alfeladatok"><CheckCircleIcon className="w-3 h-3" />{t.subtasks!.filter(s => s.done).length}/{t.subtasks!.length}</span>}
                        {(t.estimatedHours || t.actualHours) ? <span className={`inline-flex items-center gap-1 tabular-nums ${t.estimatedHours && t.actualHours && t.actualHours > t.estimatedHours ? 'text-rose-500' : ''}`} title="Ráfordított / becsült óra"><ClockIcon className="w-3 h-3" />{t.actualHours ? `${Math.round(t.actualHours * 10) / 10}h` : '0h'}{t.estimatedHours ? ` / ${t.estimatedHours}h` : ''}</span> : null}
                        {(t.comments?.length || 0) > 0 && <span className="inline-flex items-center gap-1"><ChatBubbleLeftIcon className="w-3 h-3" />{t.comments!.length}</span>}
                        {(t.tags || []).slice(0, 3).map(tag => <span key={tag} className="px-1.5 rounded bg-gray-100 dark:bg-gray-800 text-[10px] text-gray-600 dark:text-gray-300">{tag}</span>)}
                        {t.description && <span className="truncate max-w-[320px] text-gray-400 hidden lg:inline" title={t.description}>— {t.description.replace(/\s+/g, ' ').slice(0, 120)}</span>}
                      </div>
                    </button>
                    {!done && t.status !== 'in_progress' && (
                      <button type="button" onClick={() => onUpdateStatus(t.id, 'in_progress')} className="hidden sm:inline-flex crm-btn crm-btn-ghost text-xs h-7 px-2" title="Elkezdem">Elkezdem</button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

export default MyWork;
